import {
  useCreateChatMutation,
  useGetChatHistoryQuery,
  useLoadMoreChatMutation,
  useTrackImageDownloadMutation,
  otpService,
} from '../../services/agrocraftArtisan';
import { showAlert } from '../../reduxActions/alert';
import { useAppDispatch } from '../../storeHook';
import { useEffect, useState } from 'react';
import { TCreateChatRespose } from '../../utils/types/agrocraftArtisan';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  CUSTOM_EVENTS,
  appLogEvent,
} from '../../utils/analytics';
import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { updateQueryParams, downloadFile } from '../../utils/functions/common';
import { extractAgripixPath } from '../../utils/functions/string';
import { store } from '../../store';

export const useApiHelper = () => {
  const [searchParams] = useSearchParams();
  const sessionIdFromSearchParams =
    searchParams.get('sessionId');
  const [sessionId, setSessionIdState] = useState<
    number | undefined
  >(Number(sessionIdFromSearchParams));
  const [createNewChat] = useCreateChatMutation();
  const [loadMoreChat] = useLoadMoreChatMutation();
  const [trackImageDownload] = useTrackImageDownloadMutation();
  const navigate = useNavigate();

  const setSessionId: React.Dispatch<
    React.SetStateAction<number | undefined>
  > = (id) => {
    updateQueryParams({ sessionId: id }, navigate);
    setSessionIdState(id);
  };

  const { data: chatHistory } = useGetChatHistoryQuery(
    (sessionId
      ? {
        sessionId,
      }
      : undefined) ?? skipToken
  );

  const [responseChat, setResponseChat] = useState<
    TCreateChatRespose[]
  >([]);
  const [isLoadingImages, setIsLoadingImages] =
    useState(false);
  const dispatch = useAppDispatch();

  const convertRatioToOrientation = (ratio: string): string => {
    return ratio;
  };
  useEffect(() => {
    if (chatHistory?.length) {
      const history = [
        ...chatHistory.map((chat) => ({
          ChatId: chat.ID,
          ChatContents: chat.ChatContents,
          SessionId: chat.ChatSessionId,
          Brand: chat.Brand,
          LoadMore: chat.LoadMore,
          Prompt: chat.Prompt,
        })),
      ];
      setResponseChat([...history].reverse());
    } else if (sessionId && !chatHistory?.length) {
      // This handles the case where the query is still loading
      setResponseChat([]);
    }
  }, [chatHistory, sessionId]);

  const handleNewSession = (
    prompt: string,
    ratio: string,
    prompt_style: string,
    sessionId?: number
  ) => {
    setIsLoadingImages(true);
    return new Promise((resolve) => {
      const createNewChatPayload = sessionId
        ? { sessionId }
        : {};
      const orientation = convertRatioToOrientation(ratio);
      createNewChat({
        ...createNewChatPayload,
        prompt,
        ratio: orientation,
        prompt_style,
      })
        .unwrap()
        .then((res) => {
          if (typeof res != 'string') {
            const responseData = sessionId
              ? [
                {
                  ...res,
                },
              ]
              : [...responseChat, { ...res }];
            setResponseChat(responseData);
            setSessionId && setSessionId(res.SessionId);
            resolve(true);
          } else {
            dispatch(
              showAlert({
                content: res,
                severity: 'error',
              })
            );
          }
          setIsLoadingImages(false);
        })
        .catch(() => {
          setIsLoadingImages(false);
        });
    });
  };

  const handleLoadMoreChat = (
    chatId: number,
    ratio: string,
    prompt: string,
    prompt_style: string
  ) => {
    setIsLoadingImages(true);
    appLogEvent(CUSTOM_EVENTS.LOAD_MORE_IMAGES, {
      prompt,
      chatId,
      aspectRatio: ratio,
      prompt_style,
    });
    return new Promise((resolve) => {
      const orientation = convertRatioToOrientation(ratio);
      loadMoreChat({ chatId, ratio: orientation, prompt_style })
        .unwrap()
        .then((res) => {
          if (typeof res != 'string') {
            const responseData = [
              ...responseChat,
              { ...res },
            ];
            setResponseChat(responseData);
            setSessionId && setSessionId(res.SessionId);
            resolve(true);
          } else {
            dispatch(
              showAlert({
                content: res,
                severity: 'error',
              })
            );
          }
          setIsLoadingImages(false);
        })
        .catch(() => {
          setIsLoadingImages(false);
        });
    });
  };

  const logDownloadToApi = async (
    chatContentId?: number | string
  ) => {
    if (chatContentId === undefined || chatContentId === null)
      return;
    try {
      await trackImageDownload(chatContentId).unwrap();
    } catch (error) {
      console.error('Failed to track image download:', error);
    }
  };

  const downloadCrossOriginImage = async (
    imageSrc: string,
    chatContentId?: number | string
  ) => {
    appLogEvent(CUSTOM_EVENTS.DOWNLOAD_IMAGE);
    void logDownloadToApi(chatContentId);

    try {
      // If it's a blob URL, download directly
      if (imageSrc.startsWith('blob:')) {
        await downloadFile(imageSrc);
        return;
      }

      // Check if it's an agripix URL and extract the path
      const agripixPath = extractAgripixPath(imageSrc);

      if (agripixPath) {
        // Use the API to fetch the image via store dispatch
        const result = await store.dispatch(
          otpService.endpoints.getImageByLink.initiate(agripixPath)
        );

        if ('data' in result && result.data) {
          // Extract filename from path
          const pathParts = agripixPath.split('/');
          const filename = pathParts[pathParts.length - 1] || 'image.png';
          await downloadFile('', filename, async () => result.data as Blob);
        } else {
          throw new Error('Failed to fetch image');
        }
      } else {
        await downloadFile(imageSrc);
      }
    } catch (error) {
      console.error('Error downloading image:', error);
      dispatch(
        showAlert({
          content: 'Failed to download image',
          severity: 'error',
        })
      );
    }
  };

  useEffect(() => {
    if (chatHistory?.length) {
      const history = [
        ...chatHistory.map((chat) => ({
          ChatId: chat.ID,
          ChatContents: chat.ChatContents,
          SessionId: chat.ChatSessionId,
          Brand: chat.Brand,
          LoadMore: chat.LoadMore,
          Prompt: chat.Prompt,
        })),
      ];
      setResponseChat([...history].reverse());
    }
  }, [chatHistory]);

  useEffect(() => {
    if (!sessionId) setResponseChat([]);
  }, [sessionId]);

  return {
    isLoadingImages,
    responseChat,
    downloadImage: downloadCrossOriginImage,
    handleNewSession,
    sessionId,
    setSessionId,
    handleLoadMoreChat,
  };
};
